import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { buildingsApiNew as buildingsApi, Building } from '../../api';
import { BUILDINGS_LIST_PAGE_SIZE } from '../../api/buildingsList.util';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';

export const BuildingList: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const { can } = useFunctionPermissions();
  const canCreateBuilding = can('BUILDING_ADD', 'create');
  const canEditBuilding = can('BUILDING_EDIT', 'edit');
  const canDeleteBuilding = can('BUILDING_DELETE', 'delete');
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const fetchBuildings = useCallback(async () => {
    try {
      setLoading(true);
      const result = await buildingsApi.listBuildings({
        page,
        limit: BUILDINGS_LIST_PAGE_SIZE,
        search,
      });
      setBuildings(result.data);
      setTotalPages(Math.max(1, result.meta.totalPages || 1));
      setTotal(result.meta.total);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchBuildings();
  }, [fetchBuildings]);

  const handleDelete = async (id: string) => {
    if (window.confirm(t('confirmDeleteBuilding'))) {
      try {
        await buildingsApi.deleteBuilding(id);
        fetchBuildings();
      } catch (error) {
        console.error('Error deleting building:', error);
        alert(t('errorDeletingBuilding'));
      }
    }
  };

  const rowOffset = (page - 1) * BUILDINGS_LIST_PAGE_SIZE;

  if (loading && buildings.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <nav className="text-sm">
                <span className="opacity-75">🏠 / {t('buildings')} / </span>
                <span>{t('buildingList')}</span>
              </nav>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-sm">👤</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
              <h2 className="text-lg font-semibold text-gray-900 pb-3 border-b border-cyan-200 lg:border-0 lg:pb-0">
                {t('buildingList')}
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Tìm theo tên hoặc mã dự án..."
                  className="w-full sm:w-72 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                {canCreateBuilding && (
                  <Link
                    to="/buildings/add"
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {t('addBuilding')}
                  </Link>
                )}
              </div>
            </div>
          </div>

          {buildings.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m0 0h2M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <p className="text-gray-500 mb-4">{search ? 'Không tìm thấy chung cư phù hợp.' : t('noBuildingsYet')}</p>
              {canCreateBuilding && !search && (
                <Link
                  to="/buildings/add"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('addFirstBuilding')}
                </Link>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-cyan-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('stt')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('projectCode')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('buildingName')}
                      </th>
                      {isSuperAdmin && (
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('operator')}
                        </th>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('status')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('apartmentCount')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {buildings.map((building, index) => (
                      <tr key={building.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rowOffset + index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                          {building.projectCode || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {canEditBuilding ? (
                            <Link
                              to={`/buildings/${building.id}/edit`}
                              className="text-cyan-600 hover:text-cyan-800 font-medium"
                            >
                              {building.name}
                            </Link>
                          ) : (
                            <span className="text-gray-900 font-medium">{building.name}</span>
                          )}
                        </td>
                        {isSuperAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className="h-8 w-8 rounded-full bg-cyan-100 flex items-center justify-center">
                                  <span className="text-xs font-medium text-cyan-800">
                                    {building.operator?.name?.charAt(0) || 'O'}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {building.operator?.name || 'Unknown Operator'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {building.operator?.email}
                                </div>
                              </div>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              building.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {building.isActive ? t('active') : t('inactive')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {building._count?.apartments || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {(canEditBuilding || canDeleteBuilding) && (
                            <div className="flex items-center space-x-2">
                              {canEditBuilding && (
                                <Link
                                  to={`/buildings/${building.id}/edit`}
                                  className="text-indigo-600 hover:text-indigo-900"
                                  title={t('edit')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </Link>
                              )}
                              {canDeleteBuilding && (
                                <button
                                  onClick={() => handleDelete(building.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title={t('delete')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    Hiển thị {rowOffset + 1}–{Math.min(rowOffset + buildings.length, total)} / {total} chung cư
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                      disabled={page <= 1 || loading}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                    >
                      {t('previous')}
                    </button>
                    <span className="text-sm text-gray-700">
                      {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                      disabled={page >= totalPages || loading}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                    >
                      {t('next')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
